import React, { useContext, useMemo } from "react";
import { StateContext } from "../../../StateProvider";
import "./CatServicesPricing.css";
import { BackButton } from "../../../Utilities/components/Buttons";
import LetsGetStarted from "../../../Utilities/components/LetsGetStarted";
import Footer from "../../../Footer";

/* -------------------- helpers -------------------- */
function normalizeCatalog(raw) {
  var out = {};
  if (!raw) return out;
  for (var k in raw) {
    if (!Object.prototype.hasOwnProperty.call(raw, k)) continue;
    var arr = raw[k];
    if (Array.isArray(arr) && arr.length > 0) {
      var s = arr[0];
      var key = s && s.serviceName ? String(s.serviceName).toLowerCase() : String(k).toLowerCase();
      out[key] = s;
    }
  }
  return out;
}

function currency(n) {
  var val = Number(n);
  if (!isFinite(val)) return "—";
  return "$" + val.toFixed(0);
}

/* -------------------- UI Blocks -------------------- */
function SectionCard(props) {
  return (
    <section className="service-section">
      <h2 className="section-title">{props.title}</h2>
      {props.children}
    </section>
  );
}

/* ---- Membership price chips ---- */
function MembershipPriceChips(props) {
  var tiers = props.tiers || {};
  var rows = [
    { key: "club", label: "Pawsible Club", tier: tiers.club, className: "tier-club" },
    { key: "plus", label: "Pawsible Plus", tier: tiers.plus, className: "tier-plus" },
  ];
  var chips = [];
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i];
    if (r.tier && isFinite(Number(r.tier.price))) {
      chips.push(
        <span key={r.key} className={"member-price-chip " + r.className}>
          <span className="member-price-chip__label">{r.label}</span>
          <span className="member-price-chip__price">{currency(r.tier.price)}</span>
          <span className="member-price-chip__per">/ mo</span>
        </span>
      );
    }
  }
  return <div className="member-price-strip">{chips}</div>;
}

/* ---- Membership benefit matrix ---- */
function MembershipBenefitMatrix() {
  var rows = [
    { benefit: "Member Savings", club: "10%", plus: "20%", isBadge: true },
    { benefit: "Community Events", club: "✓", plus: "✓" },
    { benefit: "Health & Grooming Checks", club: "✓", plus: "✓" },
    { benefit: "Personalized Report Cards", club: "✓", plus: "✓" },
    { benefit: "Concierge & Text Support", club: "✓", plus: "✓" },
    { benefit: "Nail & Teeth Care", club: "—", plus: "✓" },
    { benefit: "Priority Booking Access", club: "—", plus: "✓" },
    { benefit: "Birthday Treat & Perks", club: "—", plus: "✓" },
  ];

  return (
    <div className="benefit-matrix-wrap">
      <div className="matrix-header">
        <span />
        <span className="matrix-col">Club</span>
        <span className="matrix-col">Plus</span>
      </div>
      <ul className="benefit-matrix">
        {rows.map((r, i) => (
          <li key={i} className="benefit-row">
            <span className="benefit-text">{r.benefit}</span>
            {r.isBadge ? (
              <>
                <span className="membership-rect-badge club-badge">{r.club}</span>
                <span className="membership-rect-badge plus-badge">{r.plus}</span>
              </>
            ) : (
              <>
                <span className={`benefit-val tier-club${r.club === "—" ? " hyphen" : ""}`}>{r.club}</span>
                <span className={`benefit-val tier-plus${r.plus === "—" ? " hyphen" : ""}`}>{r.plus}</span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---- Static Grooming Prices ---- */
const CAT_GROOMING_PRICES = {
  bath: 70,
  grooming: 130,
};

/* ---- Grooming vertical cards ---- */
function CatGroomingVertical() {
  return (
    <div className="groom-vertical-wrap">
      <div className="groom-card-vertical">
        <div className="groom-card-head">
          <div className="groom-size">All Coat Types</div>
        </div>
        <div className="groom-lines">
          <div className="groom-line">
            <span className="groom-label">Bath</span>
            <span className="groom-price">{currency(CAT_GROOMING_PRICES.bath)}</span>
          </div>
          <div className="groom-line">
            <span className="groom-label">Full Groom</span>
            <span className="groom-price">{currency(CAT_GROOMING_PRICES.grooming)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- MAIN COMPONENT -------------------- */
export default function CatServicesPricing() {
  var ctx = useContext(StateContext);
  var serviceDetails = ctx && ctx.serviceDetails ? ctx.serviceDetails : {};
  var catalog = useMemo(() => normalizeCatalog(serviceDetails), [serviceDetails]);

  function svc(name) {
    var key = String(name).toLowerCase();
    if (Object.prototype.hasOwnProperty.call(catalog, key)) return catalog[key];
    for (var alt in catalog) {
      if (!Object.prototype.hasOwnProperty.call(catalog, alt)) continue;
      if (alt.indexOf(key) !== -1 || key.indexOf(alt) !== -1) return catalog[alt];
    }
    return null;
  }

  var daycare = svc("daycare");
  var hotel = svc("hotel");
  var club = svc("pawsible club");
  var plus = svc("pawsible plus");

  // 🐱 Adjust base prices for cats
  if (daycare && daycare.price) daycare = { ...daycare, price: Math.max(daycare.price - 20, 0) }; // 55 → 35
  if (hotel && hotel.price) hotel = { ...hotel, price: Math.max(hotel.price - 34, 0) }; // 89 → 55

  return (
    <div className="services-background">
      <div className="container">
        <BackButton />
        <div className="services-container">
          <header className="pricing-header">
            <h2 className="pricing-title">Experience Pawsible: Where Every Service Nurtures Calm</h2>
            <p className="pricing-subtitle">
              Behind every lounge, grooming, and cuddle is one purpose: a calmer, more content cat.
            </p>
          </header>
          {/* Membership Section */}
          <SectionCard title="Pawsible Memberships">
            <MembershipPriceChips tiers={{ club: club, plus: plus }} />
            <MembershipBenefitMatrix />
          </SectionCard>

          {/* Daycare & Hotel */}
          <SectionCard title="Daycare & Hotel">
            <div className="daycare-boarding-grid">
              {daycare && (
                <div className="service-card">
                  <div className="service-name">Daycare</div>
                  <div className="service-price">{currency(daycare.price)}</div>
                  <p className="service-desc">{daycare.description}</p>
                </div>
              )}
              {hotel && (
                <div className="service-card">
                  <div className="service-name">Hotel</div>
                  <div className="service-price">{currency(hotel.price)}</div>
                  <p className="service-desc">{hotel.description}</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Grooming */}
          <SectionCard title="Grooming">
            <div className="grooming-responsive">
              <div className="grooming-table-wrapper">
                <table className="grooming-table">
                  <thead>
                    <tr>
                      <th>Bath</th>
                      <th>Full Groom</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{currency(CAT_GROOMING_PRICES.bath)}</td>
                      <td>{currency(CAT_GROOMING_PRICES.grooming)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <CatGroomingVertical />
            </div>
          </SectionCard>
        </div>
      </div>

      <section className='sign-up-section'>
        <LetsGetStarted />
      </section>
      <Footer />
    </div>
  );
}
